import { apiClient } from '@api/apiClient';
import { carrierStore, customerStore, orderStore } from '@store/orders';
import { CustomerContactViewModel } from '@typeDefinitions/customerTypes';

export type InsuranceFormSchema = {
  descriptionOfGoodsInsured: string | null;
  cargoValue: number | null;
  equipment: string | null;
  commodity: string | null;
  otherCommodity: string | null;
  otherEquipment: string | null;
  customer: string | null;
  insuredCompanyName: string | null;
  insuredCompanyPhone: string | null;
  insuredCompanyAddress1: string | null;
  insuredCompanyCity: string | null;
  insuredCompanyState: string | null;
  insuredCompanyZip: string | null;
  insuredCompanyCountry: string | null;
  customerContactEmail: string | null;
  confirmation: boolean;
  isGap: boolean;
  isAllRisk: boolean;
};

export type insuranceProduct = {
  id: string;
  name: string;
  description: string;
  limit: number;
  deductible: number;
  premium: number;
  serviceFee: number;
  tax: number;
  currency: string;
  commodityExclusions: string[];
  termsAndConditionsLink: string;
  statementOfFact: string;
};

export interface LoadSurePurchaseResponse {
  success: boolean;
  errors: string | null;
  certificateNumber: string;
  productId: string;
  productName: string;
  currency: string;
  limit: number;
  deductible: number;
  premium: number;
  serviceFee: number;
  tax: number;
  status: string;
  certificateLink: string;
  termsAndConditionsLink: string;
  fileClaimLink: string;
}

export interface LoadSureCertificate {
  certificateNumber: string;
  quoteLoadMode: string;
  quoteCargoValue: number;
  quoteCommodity: string;
  quoteEquipment: string;
}

export const cancelInsuranceModal = 'cancelInsuranceModal';
export const cancelInsuranceCbModal = 'cancelInsuranceCbModal';
export const requoteModal = 'requoteModal';
export const insuranceModal = 'insuranceModal';
export const loadSureEligibilityModal = 'loadSureEligibilityModal';
export const loadSureAcknowledgmentModal = 'loadSureAcknowledgmentModal';

export const cancelReasons = [
  { id: 'CANASD', name: 'Assured Changed' },
  { id: 'CANNLN', name: 'Insurance Certificate no longer needed' },
  { id: 'CANPIE', name: 'Certificate purchased in error' },
  { id: 'CANCHP', name: 'Cheaper insurance found elsewhere' },
  { id: 'CANVAL', name: 'Insured value changed' },
  { id: 'CANREQ', name: 'Insurance was not required by the shipper' },
  { id: 'CANCOP', name: 'Shipment lost to competition' },
  { id: 'CANREP', name: 'Origin/Destination/Commodity Changed' },
  { id: 'CANOTH', name: 'Other' },
];

export const isLoadSureVendorAttached = () => {
  return Boolean(
    orderStore.order.loads[orderStore.activeTab.index]
      ?.loadSureVendorContractId,
  );
};

export const hasCustomer = () => {
  return Boolean(customerStore.customer.id);
};

export const stops = () => {
  return orderStore.order.loads[orderStore.activeTab.index]?.stops ?? [];
};

export const loadId = () => {
  return orderStore.order.loads[orderStore.activeTab.index]?.id;
};

export const freightValue = () => {
  return orderStore.order.loads[orderStore.activeTab.index]?.freightValue;
};

export const isCarrierActive = () => {
  return carrierStore.carrier[orderStore.activeTab.index]?.status === 'Active';
};

export const isLoadReady = () => {
  return (
    orderStore.order.loads[orderStore.activeTab.index]?.status === 'Assigned' ||
    orderStore.order.loads[orderStore.activeTab.index]?.status ===
      'Dispatched' ||
    orderStore.order.loads[orderStore.activeTab.index]?.status ===
      'At Shipper' ||
    orderStore.order.loads[orderStore.activeTab.index]?.status === 'Can Get'
  );
};

export const loadSureVendorContractId = () => {
  return orderStore.order.loads[orderStore.activeTab.index]
    ?.loadSureVendorContractId;
};

export const loadSureCertificate = () => {
  return orderStore.order.loads[orderStore.activeTab.index]
    ?.loadSureCertificate;
};

export const isEqualValue = () => {
  if (!orderStore.order.loads[orderStore.activeTab.index]?.loadSureCertificate)
    return true;

  return (
    orderStore.order.loads[orderStore.activeTab.index]?.loadSureCertificate
      ?.quoteCargoValue ===
    orderStore.order.loads[orderStore.activeTab.index]?.freightValue
  );
};

export const hasValidPickupAndDropoff = () => {
  if (!stops().length) return false;

  let hasPickup = false;
  let hasDropoff = false;

  for (const stop of stops()) {
    if (stop.pickUp) hasPickup = true;
    if (stop.dropOff) hasDropoff = true;
  }

  return hasPickup && hasDropoff;
};

export const getAccountManagerEmail = async (id: number) => {
  try {
    const response: { value: CustomerContactViewModel } = await apiClient.get(
      `User/userinfo/${id}`,
    );
    return response.value.email;
  } catch (error) {
    return '';
  }
};
